import React, {useRef, useState} from 'react';
import Landmark from '@core/scripts/components/landmark-adapter';

import RepeatedFieldsContainer from '../../common/forms/RepeatedFieldsContainer.jsx';
import NewsletterCheckBoxField from './NewsletterCheckBoxField.jsx';
import FormSection from '../../common/forms/FormSection.jsx';
import {
    emailValidation,
    firstNameValidation,
    lastNameValidation,
    salutationValidation,
} from '@core/scripts/helper/form-input-validation/inputFieldValidations';

import {trimWhitespaces} from '@core/scripts/helper/form-input-validation/utils';
import CheckoutErrorLayer from '@core/scripts/components/layers/CheckoutErrorLayer.jsx';
import openLayer from '@core/scripts/components/layers/open-layer';
import FriendlyCaptcha from '@core/scripts/components/FriendlyCaptcha.jsx';
import {FormProvider, useForm} from 'react-hook-form';
import {doUpdateDataBackendRequest} from '@checkout/components/common/forms/helper/updatePreSignUpDataHelper.ts';
import PasswordFieldWithProgressBar from '@checkout/components/common/forms/fields/passwordFieldWithProgressBar/PasswordFieldWithProgressBar.jsx';
import {doSendMetric} from '@core/scripts/helper/send-metric-helper.js';
import {urls} from '../urlHelper.js';
import Toggle from './Toggle.jsx';
import {Alert, Button} from '@bonprix/design-system';
import Device from '@core/scripts/components/device';
import CheckboxWithToolTipField from '@checkout/components/common/CheckboxWithToolTipField.jsx';
import {PermanentLoginConfirmationTipText} from '@checkout/components/login/PermanentLoginToolTipText.jsx';
import ControlledInputField from '@checkout/components/common/forms/fields/ControlledInputField.jsx';
import ControlledSelectDropdown from '@checkout/components/common/forms/fields/ControlledSelectDropdown.tsx';
import {
    trackClickRegistration,
    trackClickStayLoggedInCheckBoxStatus,
    trackClickStayLoggedInInfo,
    trackViewStayLoggedInCheckBox,
} from '@checkout/components/helper/TrinityTrackingHelper.js';

export default function PreSignUpForm({
    texts,
    siteKeyPresignup,
    isCaptchaEnabled,
    captchaLang,
    isGuestCheckoutEnabled,
    onGlobalError,
    guestLoginPageLandmark,
    preSignUpPageLandmark,
    isGuest,
    onGuestSwitchToggle,
    isShowNewsletter,
}) {
    /* resetSwitch is a toggle which makes friendlyCaptcha reset and load a new captcha token after error */
    const [resetSwitch, setResetSwitch] = useState(false);

    const [captchaCompletedSuccessfully, setCaptchaCompletedSuccessfully] = useState(false);
    const [startMode, setStartMode] = useState('focus');
    const [loading, setLoading] = useState(false);
    const captchaTokenRef = useRef('');
    const captchaStartRef = useRef(0);

    const defaultValues = {
        salutation: 'FEMALE',
        firstName: '',
        lastName: '',
        email: '',
        emailRepeat: '',
        password: '',
        receiveNewsletter: '',
        permanentLoginConsent: '',
    };

    const {
        control,
        register,
        handleSubmit,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: {errors, isValid, isSubmitSuccessful, isSubmitted},
        trigger,
    } = useForm({
        mode: 'onTouched',
        defaultValues: defaultValues,
    });

    const onSubmit = (dataUpdateRequest) => {
        let guestEnabledAndSelected = isGuest && isGuestCheckoutEnabled; //just a safeguard to make sure guest is only true if guest checkout is enabled
        if (guestEnabledAndSelected) {
            delete dataUpdateRequest.salutation;
            delete dataUpdateRequest.password;
        }
        setLoading(true);
        onGlobalError('');

        const encodedValues = {
            ...dataUpdateRequest,
            firstName: trimWhitespaces(dataUpdateRequest.firstName),
            lastName: trimWhitespaces(dataUpdateRequest.lastName),
            email: dataUpdateRequest.email.trim(),
            emailRepeat: dataUpdateRequest.emailRepeat.trim(),
        };

        const allValues = {
            ...encodedValues,
            captchaToken: captchaTokenRef.current,
        };

        const requestBody = isCaptchaEnabled ? allValues : encodedValues;

        doUpdateDataBackendRequest(
            texts,
            requestBody,
            guestEnabledAndSelected ? urls.PRE_SIGNUP_GUEST : urls.PRE_SIGNUP,
            onGlobalError,
            handleEmailNotAllowedError,
            handleValidationErrors,
            handleResetCaptcha,
            handleRequestEnd,
            handleGeneralError,
        );
    };

    const handleEmailNotAllowedError = () => {
        setError('email', {message: texts['email_not_allowed']});
    };

    const handleValidationErrors = (validationErrors) => {
        validationErrors.forEach((error) => setError(error.name, {message: texts[error.message]}));
    };

    const handleResetCaptcha = () => {
        setLoading(false);
        setStartMode('auto');
        setResetSwitch(!resetSwitch);
    };

    const handleRequestEnd = () => {
        setLoading(false);
    };

    const handleGeneralError = () => {
        setLoading(false);
        openLayer(CheckoutErrorLayer, {texts});
    };

    const handleCaptchaStart = () => {
        captchaStartRef.current = Date.now();
    };

    const handleVerifyCallback = (solution) => {
        captchaTokenRef.current = solution;
        doSendMetric('captcha.signup.duration', Date.now() - captchaStartRef.current);
        !!solution && setCaptchaCompletedSuccessfully(true);
    };

    const handleErrorCallback = (error) => {
        setCaptchaCompletedSuccessfully(true);
        doSendMetric('captcha.signup.error', 1.0);
    };

    const salutationChoices = [
        {value: 'FEMALE', label: texts['cus__salutation_female']},
        {value: 'MALE', label: texts['cus__salutation_male']},
        {value: 'DIVERSE', label: texts['cus__salutation_diverse']},
        {value: 'UNSPECIFIED', label: texts['cus__salutation_unspecified']},
    ];

    const newsletterClickLandmark = (isNewsletterChecked) => {
        const landmarkValue = isNewsletterChecked ? 'ja' : 'nein';
        Landmark.sendClickLandmark(`nk_NL${landmarkValue}`);
    };

    const formErrorsObjectIsNotEmpty = () => {
        return Object.entries(errors).length > 0;
    };

    const handleToggleGuest = (sliderIsTurnedToGuest) => {
        onGuestSwitchToggle();
        if (sliderIsTurnedToGuest) {
            Landmark.sendPageLandmark(guestLoginPageLandmark);
            handleSendClickToggle('guest_on');
            clearErrors('password');
        } else {
            Landmark.sendPageLandmark(preSignUpPageLandmark);
            handleSendClickToggle('guest_off');
        }
    };

    const trinityPageName = 'register';

    const trackStayLoginCheckboxChange = (isActivated) => {
        trackClickStayLoggedInCheckBoxStatus(trinityPageName, isActivated);
    };

    const trackStayLoginTooltipOpened = (changedToOpen) => {
        if (changedToOpen) {
            trackClickStayLoggedInInfo(trinityPageName);
        }
    };

    const trackStayLoginCheckboxIsDisplayed = () => {
        const handleTabChange = (event) => {
            const {activeTab} = event.detail;
            if (activeTab === trinityPageName) {
                trackViewStayLoggedInCheckBox(trinityPageName);
            }
        };

        window.addEventListener('viewTab', handleTabChange);

        return () => {
            window.removeEventListener('viewTab', handleTabChange);
        };
    };

    const handleSendClickToggle = (clickName) => {
        trackClickRegistration('login', clickName);
    };

    return (
        <FormProvider
            register={register}
            formState={{errors, isValid, isSubmitSuccessful, isSubmitted}}
            getValues={getValues}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            trigger={trigger}
        >
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-0 mb-12 mt-6 m:mt-8 mx-auto m:w-8/12 xl:w-3/5">
                {!!isGuestCheckoutEnabled && (
                    <Toggle
                        label={texts['cus__presignup_continue_as_guest']}
                        checked={!!isGuest}
                        onChange={handleToggleGuest}
                    />
                )}

                {!isGuest && (
                    <ControlledSelectDropdown
                        control={control}
                        name={'salutation'}
                        validation={salutationValidation(texts)}
                        options={salutationChoices}
                        label={texts['cot09_salutation']}
                        className=""
                    />
                )}
                <Alert isOpen={!!isGuest} status={'info'} showCloseIcon={false}>
                    <div>
                        <p>{texts['cus__presignup_continue_as_guest_hint_label']}</p>
                        <div className="ml-[25px]">
                            <ul className="list-disc">
                                <li>{texts['cus__presignup_continue_as_guest_hint_bulletpoint_1']}</li>
                                <li>{texts['cus__presignup_continue_as_guest_hint_bulletpoint_2']}</li>
                                <li>{texts['cus__presignup_continue_as_guest_hint_bulletpoint_3']}</li>
                            </ul>
                        </div>
                    </div>
                </Alert>

                <FormSection first>
                    <ControlledInputField
                        name={'firstName'}
                        control={control}
                        validation={firstNameValidation(texts)}
                        type={'text'}
                        label={`${texts['cot09_firstname']}*`}
                        onFocus={() => Landmark.sendClickLandmark('start_enter_first_name')}
                    />
                    <ControlledInputField
                        name={'lastName'}
                        control={control}
                        validation={lastNameValidation(texts)}
                        type={'text'}
                        label={`${texts['cot09_surname']}*`}
                        onFocus={() => Landmark.sendClickLandmark('start_enter_last_name')}
                    />
                </FormSection>

                <FormSection>
                    <RepeatedFieldsContainer message={texts['presignup.emailrepeat.invalid']}>
                        <ControlledInputField
                            type="email"
                            control={control}
                            name="email"
                            label={`${texts['cot09_email_address']}*`}
                            validation={emailValidation(texts)}
                            onFocus={() => Landmark.sendClickLandmark('start_enter_mail')}
                        />
                        <ControlledInputField
                            name="emailRepeat"
                            control={control}
                            type="email"
                            label={`${texts['cot09_email_address_repetition20']}*`}
                            validation={emailValidation(texts)}
                        />
                    </RepeatedFieldsContainer>
                </FormSection>

                {!isGuest && (
                    <FormSection>
                        <PasswordFieldWithProgressBar
                            control={control}
                            texts={texts}
                            name="password"
                            label={`${texts['cot09_password']}*`}
                        />
                    </FormSection>
                )}

                {!Device.isApp() && !isGuest && (
                    <FormSection>
                        <CheckboxWithToolTipField
                            control={control}
                            name="permanentLoginConsent"
                            checkboxLabel={texts.cus__permanentLogin_checkbox_label}
                            toolTipText={<PermanentLoginConfirmationTipText texts={texts} pageName={trinityPageName} />}
                            viewTrackingCallback={trackStayLoginCheckboxIsDisplayed}
                            changeCheckboxTrackingCallback={trackStayLoginCheckboxChange}
                            tooltipTrackingCallback={trackStayLoginTooltipOpened}
                        />
                    </FormSection>
                )}

                {isCaptchaEnabled && (
                    <FormSection last={isGuest && true}>
                        <FriendlyCaptcha
                            lang={captchaLang}
                            siteKey={siteKeyPresignup}
                            verifyCallback={handleVerifyCallback}
                            errorCallback={handleErrorCallback}
                            startMode={startMode}
                            resetSwitch={resetSwitch}
                            onCaptchaStart={handleCaptchaStart}
                        />
                    </FormSection>
                )}

                {isShowNewsletter && (
                    <FormSection last>
                        <NewsletterCheckBoxField
                            control={control}
                            name="receiveNewsletter"
                            label={texts['cus__presignup_newsletter_label']}
                            benefit1={texts['cus__presignup_newsletter_benefit_1']}
                            benefit2={texts['cus__presignup_newsletter_benefit_2']}
                            changeCheckboxTrackingCallback={newsletterClickLandmark}
                        />
                    </FormSection>
                )}
                <Button
                    loading={loading}
                    type={'submit'}
                    variant={'primary'}
                    icon={'arrow-right'}
                    iconPosition={'end'}
                    disabled={formErrorsObjectIsNotEmpty() || (isCaptchaEnabled && !captchaCompletedSuccessfully)}
                >
                    {texts['cot09_continue']}
                </Button>
            </form>
        </FormProvider>
    );
}
